import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@shopify/polaris";
import { ChevronLeftIcon, ChevronRightIcon } from "@shopify/polaris-icons";
import { RootState, AppDispatch } from "configureStore";
import UserAvatar from "components/user-avatar/user-avatar";
import {
  setModalxComponent,
  setModalxComponentProps,
  setIsModalxOpen,
} from "components/modalx/modalx.slice";
import { sendHeartbeat } from "services/presence.service";
import { fetchOnlineThunk, toggleExpanded, setExpanded, restoreExpanded } from "./presence.slice";
import { useCompanyPresenceSocket } from "./use-company-presence-socket";
import OnlineTab from "./online-tab";
import ChatTab from "features/chat/chat-tab";
import {
  setActiveTab,
  openConversation,
  fetchConversationsThunk,
  fetchUnreadByCompanyThunk,
  clearCrossCompanyUnread,
} from "features/chat/chat.slice";
import { useChatSocket } from "features/chat/use-chat-socket";
import { fetchSnapshotThunk } from "features/recognition/recognition.slice";
import { useRecognitionSocket } from "features/recognition/use-recognition-socket";
import LevelBadge from "features/recognition/level-badge";
import useIsBelowDesktop from "components/layout/use-is-below-desktop";
import { COLLAPSED_W, EXPANDED_W } from "./online-users-panel.constants";
import { usePresenceRailWidth } from "./use-presence-rail-width";

const HEARTBEAT_MS = 60_000;
const POLL_MS = 30_000;

const OnlineUsersPanel: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const { pathname } = useLocation();
  // Below 1024px there is no rail: the list is a sheet over the page, opened from the top bar.
  const isBelowDesktop = useIsBelowDesktop();
  const railWidth = usePresenceRailWidth(companyId);
  const { self, online, offline, expanded } = useSelector(
    (state: RootState) => state.presenceReducer
  );
  const { activeTab, conversations, openConversationCompanyId } = useSelector(
    (state: RootState) => state.chatReducer
  );
  const { snapshot, snapshotCompanyId } = useSelector(
    (state: RootState) => state.recognitionReducer
  );
  const recognitionSnapshot =
    snapshotCompanyId === companyId ? snapshot : null;

  // Heartbeat — every minute, regardless of tab visibility
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        await sendHeartbeat();
      } catch {
        /* network blip — try again next interval */
      }
    };
    tick();
    const id = window.setInterval(() => {
      if (!cancelled) tick();
    }, HEARTBEAT_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  // Real-time updates via socket (subscribe to company room + listen for events).
  // Poll below remains as safety net in case socket is unavailable.
  useCompanyPresenceSocket(companyId);

  // Chat sockets must listen even when the user is on the Online tab so that
  // unread badges update in the background.
  useChatSocket(companyId);

  // Recognition events: balance / karma updates, plus toast for received kudos.
  useRecognitionSocket(companyId);

  // Initial snapshot for the balance pill in self-section. Side effect of the
  // request also performs the daily-grant refresh on the backend.
  useEffect(() => {
    if (companyId) dispatch(fetchSnapshotThunk(companyId));
  }, [companyId, dispatch]);

  // Side-panel thread is scoped to the route company. Clear a stale open thread
  // from another company so the chats tab does not show the wrong context.
  useEffect(() => {
    if (!companyId) return;
    if (openConversationCompanyId && openConversationCompanyId !== companyId) {
      dispatch(
        openConversation({ companyId, conversationId: null, skipPersist: true })
      );
    }
  }, [companyId, openConversationCompanyId, dispatch]);

  // Conversations list must be loaded before the chats tab is opened so the
  // chevron's total-unread badge is correct from the start.
  useEffect(() => {
    if (!companyId) return;
    dispatch(fetchConversationsThunk(companyId));
    // Switching to a company resets its cross-company badge — the user is
    // now actively viewing it, and per-conversation unread covers everything.
    dispatch(clearCrossCompanyUnread(companyId));
    // Refresh cross-company snapshot for OTHER companies.
    dispatch(fetchUnreadByCompanyThunk());
  }, [companyId, dispatch]);

  // Poll online list — every 30s, only when companyId is known
  const lastPollCompany = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!companyId) return;
    lastPollCompany.current = companyId;
    dispatch(fetchOnlineThunk(companyId));
    const id = window.setInterval(() => {
      if (lastPollCompany.current === companyId) {
        dispatch(fetchOnlineThunk(companyId));
      }
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [companyId, dispatch]);

  // Published as a CSS var so anything outside this component's own tree —
  // the toast stack in App.tsx is the first of these — can keep clear of the
  // rail without importing its width constants. 0 where there is no rail.
  useEffect(() => {
    document.documentElement.style.setProperty("--presence-rail-width", `${railWidth}px`);
    return () => {
      document.documentElement.style.removeProperty("--presence-rail-width");
    };
  }, [railWidth]);

  // The sheet covers the page, so going to another page closes it instead of leaving it
  // open over the new page.
  useEffect(() => {
    if (isBelowDesktop) dispatch(setExpanded(false));
  }, [pathname, isBelowDesktop, dispatch]);

  // A tablet turned sideways gets the rail: it must not open as the sheet was left.
  useEffect(() => {
    if (!isBelowDesktop) dispatch(restoreExpanded());
  }, [isBelowDesktop, dispatch]);

  if (!companyId) return null;
  if (isBelowDesktop && !expanded) return null;

  const width = expanded ? EXPANDED_W : COLLAPSED_W;
  const avatarSize = expanded ? 44 : 40;

  // Clicking YOURSELF still opens your card — there is no chat with yourself to
  // open instead. For everyone else the list opens the conversation, and the
  // card moved to their name in the chat header.
  const handleSelfClick = () => {
    if (!self) return;
    dispatch(setModalxComponent("UserPublicProfileModal"));
    dispatch(setModalxComponentProps({ companyId, userId: self.id }));
    dispatch(setIsModalxOpen(true));
  };

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
  const totalOnline = online.length + (self ? 1 : 0);

  return (
    <>
      {expanded && (
        <div
          data-testid="presence-backdrop"
          onClick={() => dispatch(setExpanded(false))}
          className="fixed inset-0 bg-black/30 z-30 transition-opacity duration-200"
          aria-hidden="true"
        />
      )}
      <div
        data-testid="presence-rail"
        className={`fixed right-0 bottom-[var(--phone-bar-height,0px)] z-40 bg-gray-900 text-gray-100 transition-[width] duration-200 ease-out overflow-hidden flex flex-col ${
          isBelowDesktop ? "w-full sm:w-[22.5rem]" : ""
        }`}
        style={{
          width: isBelowDesktop ? undefined : width,
          top: "var(--dashboard-topbar-height, 4rem)",
        }}
      >
        {/* Header with toggle button + counter */}
        <div className="border-b border-gray-700 flex items-center justify-between px-2 py-1.5 min-h-[44px]">
          <button
            type="button"
            onClick={() => dispatch(toggleExpanded())}
            className="relative p-1.5 rounded hover:bg-gray-800 text-gray-300 max-lg:flex max-lg:min-h-11 max-lg:min-w-11 max-lg:items-center max-lg:justify-center"
            title={expanded ? t("collapse") : t("expand")}
            aria-label={expanded ? t("collapse") : t("expand")}
          >
            <span className="block w-5 h-5">
              <Icon source={(expanded ? ChevronRightIcon : ChevronLeftIcon) as any} tone="subdued" />
            </span>
            {!expanded && totalUnread > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[11px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-tight ring-2 ring-gray-900">
                {totalUnread > 99 ? "99+" : totalUnread}
              </span>
            )}
          </button>
          {expanded && (
            <span className="text-[11px] uppercase tracking-wide text-gray-400 pr-2 truncate">
              {activeTab === "online"
                ? t("online_count", {
                    count: totalOnline,
                    defaultValue: `${totalOnline} online`,
                  })
                : t("chats")}
            </span>
          )}
        </div>

        {/* Self section — always visible */}
        {self && (
          <div className="bg-gray-800 border-b border-gray-700 shrink-0">
            <div
              onClick={handleSelfClick}
              className="px-3 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-700"
            >
              <UserAvatar
                companyId={companyId}
                user={{ id: self.id, name: self.name, avatarFileId: self.avatarFileId }}
                size={avatarSize}
                online
              />
              {expanded && (
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{self.name}</div>
                  <div className="text-xs text-green-400 flex items-center gap-2">
                    <span>{t("online")}</span>
                    {recognitionSnapshot && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/app/${companyId}/recognition`);
                        }}
                        className="px-1.5 py-0.5 rounded-full bg-gray-700 hover:bg-gray-600 text-gray-100 text-[10px] font-semibold"
                        title={t("kudos_balance_tooltip", {
                          defaultValue: "Kudos to give today (resets every day)",
                        })}
                      >
                        ⚡ {recognitionSnapshot.giveBalance}
                      </button>
                    )}
                  </div>
                  {recognitionSnapshot && (
                    <div className="mt-1">
                      <LevelBadge level={recognitionSnapshot.level} size="sm" />
                    </div>
                  )}
                </div>
              )}
            </div>
            {expanded && !self.avatarFileId && (
              <div
                className="px-3 py-2 bg-amber-900/40 border-t border-amber-800 text-xs text-amber-200 cursor-pointer hover:bg-amber-900/60"
                onClick={() => navigate(`/app/${companyId}/profile/${self.id}/info`)}
              >
                {t("avatar_upload_prompt")}
              </div>
            )}
          </div>
        )}

        {/* Tabs (only when expanded) */}
        {expanded && (
          <div className="flex border-b border-gray-700 shrink-0">
            <button
              type="button"
              onClick={() => dispatch(setActiveTab("online"))}
              className={`flex-1 py-2 max-lg:min-h-11 text-xs uppercase tracking-wide ${
                activeTab === "online"
                  ? "bg-gray-800 text-gray-100 border-b-2 border-blue-500"
                  : "text-gray-400 hover:bg-gray-800"
              }`}
            >
              {t("online")}
            </button>
            <button
              type="button"
              onClick={() => dispatch(setActiveTab("chats"))}
              className={`flex-1 py-2 max-lg:min-h-11 text-xs uppercase tracking-wide flex items-center justify-center gap-1.5 ${
                activeTab === "chats"
                  ? "bg-gray-800 text-gray-100 border-b-2 border-blue-500"
                  : "text-gray-400 hover:bg-gray-800"
              }`}
            >
              {t("chats")}
              {totalUnread > 0 && (
                <span className="bg-red-500 text-white text-[9px] font-semibold px-1 py-px rounded-full min-w-[14px] text-center leading-tight">
                  {totalUnread > 99 ? "99+" : totalUnread}
                </span>
              )}
            </button>
          </div>
        )}

        {/* Tab content */}
        <div
          className={
            "flex-1 overflow-y-auto overflow-x-hidden " +
            (expanded
              ? "[scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-gray-700 [&::-webkit-scrollbar-track]:bg-transparent"
              : "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden")
          }
        >
          {/* Collapsed view always shows the online list (avatars only) regardless of activeTab */}
          {!expanded || activeTab === "online" ? (
            <div className="py-2">
              <OnlineTab
                companyId={companyId}
                expanded={expanded}
                avatarSize={avatarSize}
                online={online}
                offline={offline}
              />
            </div>
          ) : (
            <ChatTab companyId={companyId} expanded={expanded} />
          )}
        </div>
      </div>
    </>
  );
};

export default OnlineUsersPanel;
